import React, { useState, useEffect, useRef } from "react";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import {
  Grid,
  Card,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  StyledTextField,
  StyledButton,
  OutlinedIconFab,
  theme,
} from "../StyledComponents";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import MicOffIcon from "@mui/icons-material/MicOff";
import ClearIcon from "@mui/icons-material/Clear";
import { toast } from "react-toastify";
import Icon from "../Utils/Icons";
import { AdultVaccinationTable } from "./AdultVaccinationTable";
import { formatDateYYYYMMDD } from "../Utils/UtilsFunctions";
import VaccinationDateField from "../Utils/VaccinationDateField";
import { FIELD_SET_IDS } from "../config/defaults/fieldLayoutUtils";
import useSectionFieldLayout from "../config/hooks/useSectionFieldLayout";

const AdultVaccination = ({
  sectionName,
  activeSection,
  // QuickConsultation renders this without the open/focused split — keep its old behaviour.
  isFocusedSection = activeSection,
  data,
  setData,
  startRecording,
  stopRecording,
  isAiEnabled,
  fieldLayout: fieldLayoutProp,
}) => {
  const fieldLayout = useSectionFieldLayout(
    fieldLayoutProp,
    FIELD_SET_IDS.adultVaccination,
  );
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const headers = {
    Authorization: `Bearer ${authToken}`,
  };

  const [vaccineName, setVaccineName] = useState("");
  const [dose, setDose] = useState("");
  const [dateOfAdministration, setDateOfAdministration] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [vaccinations, setVaccinations] = useState([]);

  useEffect(() => {
    if (data) {
      if (Array.isArray(data)) {
        setVaccinations(data);
      } else {
        // Handle history object structure { prev, new }
        const combined = [...(data.prev || []), ...(data.new || [])];
        setVaccinations(combined);
      }
    }
  }, [data]);

  const [validationErrors, setValidationErrors] = useState({});
  const { transcript, resetTranscript } = useSpeechRecognition();
  const [listeningOn, setListeningOn] = useState(false);

  const [autocompleteData, setAutoCompleteData] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selectedSource, setSelectedSource] = useState("custom");
  const [selectedId, setSelectedId] = useState(null);
  const [debounceFunctionCalled, setDebounceFunctionCalled] = useState(false);
  const [mouseDownOnOption, setMouseDownOnOption] = useState(false);
  const [termSelectedOrAdded, setTermSelectedOrAdded] = useState(false);
  const optionChosen = useRef(false);
  const skipEffect = useRef(false);


  useEffect(() => {
    setAuthToken(sessionStorage.getItem("accessToken"));
  }, [sessionStorage.getItem("accessToken")]);

  useEffect(() => {
    // Dictation goes to the focused section only — several sections can be open at once.
    if (isFocusedSection) {
      let timerId;
      timerId = setTimeout(() => {
        setVaccineName(transcript);
      }, 3000);
      return () => {
        clearTimeout(timerId);
      };
    }
  }, [transcript]);

  useEffect(() => {
    if (skipEffect.current) {
      skipEffect.current = false;
      return;
    }

    let timerOut = setTimeout(() => {
      if (vaccineName.trim() !== "") {
        debouncedVaccineFilterCall(vaccineName.toLowerCase());
      }
    }, 500);
    return () => clearTimeout(timerOut);
  }, [vaccineName]);

  const debouncedVaccineFilterCall = async (name) => {
    console.log("Debounce function called");
    try {
      if (name.length > 1) {
        const response = await axios.get(
          `${REACT_APP_BACKEND_ROUTE_URL}search_vaccines/?name=${name}`,
          { headers: headers }
        );
        if (
          response &&
          response.data.status === "Success" &&
          response.data.data.length > 0
        ) {
          const mappedData = response.data.data.map((item) => ({
            term: item.term,
            id: item.conceptId || item.id,
            source: item.source || "database",
          }));
          setAutoCompleteData(mappedData);
          setFilteredOptions(mappedData);
        } else {
          setAutoCompleteData([]);
          setFilteredOptions([]);
        }
        setDebounceFunctionCalled(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const validateFields = () => {
    const errors = {};
    if (
      fieldLayout.isFieldVisible("vaccine_name") &&
      fieldLayout.isFieldRequired("vaccine_name") &&
      !vaccineName.trim()
    ) {
      errors.vaccineName = fieldLayout.getValidationMessage(
        "vaccine_name",
        "Vaccine Name is required.",
      );
    }
    if (
      fieldLayout.isFieldVisible("dose") &&
      fieldLayout.isFieldRequired("dose") &&
      !dose
    ) {
      errors.dose = fieldLayout.getValidationMessage(
        "dose",
        "Dose is required.",
      );
    }
    if (
      fieldLayout.isFieldVisible("date_of_administration") &&
      fieldLayout.isFieldRequired("date_of_administration") &&
      !dateOfAdministration
    ) {
      errors.dateOfAdministration = fieldLayout.getValidationMessage(
        "date_of_administration",
        "Date of Administration is required.",
      );
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAdd = async () => {
    if (!validateFields()) return;

    let finalId = null;
    let currentSource = "custom";

    if (!optionChosen.current) {
      try {
        const response = await axios.get(
          `${REACT_APP_BACKEND_ROUTE_URL}search_vaccines/?name=${vaccineName}`,
          { headers: headers }
        );
        const found = response?.data?.data?.find(
          (v) => v.term.toLowerCase() === vaccineName.toLowerCase()
        );
        if (found) {
          finalId = found.conceptId || found.id || null;
          currentSource = found.source || "database";
        } else {
          // New vaccine not in database, will be added during final save
          finalId = null;
          currentSource = "custom";
        }
      } catch (err) {
        console.error(err);
      }
    } else {
      currentSource = selectedSource;
      finalId = selectedId;
    }

    console.log(`Adding Data - Source: ${currentSource}, ID: ${finalId}`);

    const newEntry = {
      vaccine_name: vaccineName,
      dose: dose,
      date_of_administration: dateOfAdministration
        ? formatDateYYYYMMDD(dateOfAdministration)
        : "",
      id: finalId,
      source: currentSource,
    };

    if (Array.isArray(data)) {
      const updatedData = [...vaccinations, newEntry];
      setVaccinations(updatedData);
      setData(updatedData);
    } else {
      const updatedNew = [...(data.new || []), newEntry];
      setData({
        ...data,
        new: updatedNew,
      });
      // if (isolateSessionEntries) {
      //   setVaccinations(updatedNew);
      // }
    }
    clearForm();
    optionChosen.current = false;
    setFilteredOptions([]);
  };

  const handleUpdate = async () => {
    if (!validateFields()) return;

    let finalId = null;
    let currentSource = "custom";

    if (!optionChosen.current) {
      try {
        const response = await axios.get(
          `${REACT_APP_BACKEND_ROUTE_URL}search_vaccines/?name=${vaccineName}`,
          { headers: headers }
        );
        const found = response?.data?.data?.find(
          (v) => v.term.toLowerCase() === vaccineName.toLowerCase()
        );
        if (found) {
          finalId = found.conceptId || found.id || null;
          currentSource = found.source || "database";
        } else {
          const addResponse = await axios.post(
            `${REACT_APP_BACKEND_ROUTE_URL}search_vaccines/`,
            { term: vaccineName },
            { headers: headers }
          );
          if (addResponse.data.status === "Success") {
            finalId = addResponse.data.data.conceptId || addResponse.data.data.id || null;
          }
          currentSource = "custom";
        }
      } catch (err) {
        console.error(err);
      }
    } else {
      currentSource = selectedSource;
      finalId = selectedId;
    }

    console.log(`Updating Data - Source: ${currentSource}, ID: ${finalId}`);

    const updatedEntry = {
      vaccine_name: vaccineName,
      dose: dose,
      date_of_administration: dateOfAdministration
        ? formatDateYYYYMMDD(dateOfAdministration)
        : "",
      id: finalId,
      source: currentSource,
    };

    if (Array.isArray(data)) {
      const updatedData = vaccinations.map((item, index) =>
        index === editingIndex ? updatedEntry : item
      );
      setVaccinations(updatedData);
      setData(updatedData);
    } else {
      // It's a history object { prev, new }
      // We need to figure out if we're editing a 'new' item or 'prev' item
      // For simplicity in this UI, we usually only allow editing 'new' items
      // But if we want to support editing both, we'd need more logic.
      // Assuming we only add to 'new' and can edit what we added.
      const prevCount = (data.prev || []).length;
      if (editingIndex >= prevCount) {
        const newIndex = editingIndex - prevCount;
        const updatedNew = (data.new || []).map((item, index) =>
          index === newIndex ? updatedEntry : item
        );
        setData({
          ...data,
          new: updatedNew,
        });
      }
    }

    setEditingIndex(null);
    clearForm();
    optionChosen.current = false;
    setFilteredOptions([]);
  };

  const handleEditRow = (index) => {
    const item = vaccinations[index];
    setVaccineName(item.vaccine_name);
    setDose(item.dose);
    setDateOfAdministration(
      item.date_of_administration
        ? formatDateYYYYMMDD(item.date_of_administration)
        : ""
    );
    setEditingIndex(index);
    optionChosen.current = true;
    setSelectedSource(item.source || "database");
  };

  const handleDeleteRow = (index) => {
    if (Array.isArray(data)) {
      const updatedData = vaccinations.filter((_, i) => i !== index);
      setVaccinations(updatedData);
      setData(updatedData);
    } else {
      const prevList = data.prev || [];
      const newList = data.new || [];
      const prevCount = prevList.length;
      if (index < prevCount) {
        const updatedPrev = prevList.filter((_, i) => i !== index);
        const next = { ...data, prev: updatedPrev, new: [...newList] };
        setVaccinations([...updatedPrev, ...newList]);
        setData(next);
      } else {
        const newIndex = index - prevCount;
        const updatedNew = newList.filter((_, i) => i !== newIndex);
        const next = { ...data, prev: [...prevList], new: updatedNew };
        setVaccinations([...prevList, ...updatedNew]);
        setData(next);
      }
    }
  };

  const clearForm = () => {
    setVaccineName("");
    setDose("");
    setDateOfAdministration("");
    setValidationErrors({});
    setEditingIndex(null);
    setFilteredOptions([]);
    setDebounceFunctionCalled(false);
    setTermSelectedOrAdded(false);
  };

  const handleClearName = () => {
    setValidationErrors({});
    setVaccineName("");
    resetTranscript();
    setFilteredOptions([]);
    setDebounceFunctionCalled(false);
    setTermSelectedOrAdded(false);
  };

  return (
    <ThemeProvider theme={theme}>
      {activeSection && (
        <Card
          sx={{
            borderRadius: "10px",
            boxShadow: "0 4px 4px 0 #C7C7CC inset",
            backgroundColor: "#FFF",
            padding: "20px",
            marginBottom: "20px",
          }}
        >
          <Grid container spacing={2}>
            {fieldLayout.isFieldVisible("vaccine_name") && (
            <Grid item {...fieldLayout.getFieldGrid("vaccine_name", { xs: 12, sm: 5 })}>
              <StyledTextField
                fullWidth
                label={fieldLayout.getFieldLabel("vaccine_name", "Vaccine Name")}
                value={vaccineName}
                onChange={(e) => {
                  setVaccineName(e.target.value);
                  optionChosen.current = false;
                }}
                onBlur={() => {
                  setTimeout(() => {
                    if (!mouseDownOnOption) {
                      setFilteredOptions([]);
                      setDebounceFunctionCalled(false);
                      setTermSelectedOrAdded(false);
                    }
                    setMouseDownOnOption(false);
                  }, 100);
                }}
                onFocus={() => {
                  if (vaccineName.trim() !== "") {
                    debouncedVaccineFilterCall(vaccineName.toLowerCase());
                  }
                }}
                error={!!validationErrors.vaccineName}
                helperText={validationErrors.vaccineName}
                InputProps={{
                  endAdornment: (
                    <div
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <div style={{ marginRight: "5px" }}>
                        {fieldLayout.isCapabilityEnabled("vaccine_name", "clear") && (
                        <Tooltip title="Clear">
                          <OutlinedIconFab
                            size="small"
                            variant="contained"
                            onClick={handleClearName}
                          >
                            <Icon name="clearTextField" size={32} />
                          </OutlinedIconFab>
                        </Tooltip>
                        )}
                      </div>
                      <>
                        {isAiEnabled &&
                          fieldLayout.isCapabilityEnabled("vaccine_name", "voice") &&
                          (listeningOn === false ? (
                            <Tooltip title="Start Listening">
                              <OutlinedIconFab
                                onClick={() => {
                                  startRecording();
                                  setListeningOn(true);
                                }}
                                variant="contained"
                                size="small"
                              >
                                <Icon name="micNotInUse" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Stop Listening">
                              <OutlinedIconFab
                                onClick={() => {
                                  stopRecording("Adult Vaccination");
                                  setListeningOn(false);
                                }}
                                variant="outlined"
                                size="small"
                              >
                                <Icon name="micInUse" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                          ))}
                      </>
                    </div>
                  ),
                }}
              />
              {filteredOptions.length > 0 ? (
                <div
                  style={{
                    backgroundColor: "#f5f5f5",
                    borderRadius: "10px",
                    padding: "10px",
                    maxHeight: "200px",
                    overflowY: "auto",
                    boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                    marginTop: "5px",
                  }}
                >
                  {filteredOptions.map((op, idx) => (
                    <div key={idx}>
                      <StyledButton
                        sx={{
                          textAlign: "left",
                          justifyContent: "flex-start",
                          width: "100%",
                          color: "black",
                          backgroundColor: "transparent",
                          "&:hover": {
                            backgroundColor: "#e0e0e0",
                          },
                        }}
                        onClick={() => {
                          skipEffect.current = true;
                          optionChosen.current = true;
                          setVaccineName(op.term);
                          setFilteredOptions([]);
                          setTermSelectedOrAdded(true);
                          setDebounceFunctionCalled(false);
                          setSelectedSource(op.source || "database");
                          setSelectedId(op.id || null);
                        }}
                        onMouseDown={() => setMouseDownOnOption(true)}
                      >
                        {op.term}
                      </StyledButton>
                    </div>
                  ))}
                </div>
              ) : filteredOptions.length === 0 &&
                debounceFunctionCalled &&
                !termSelectedOrAdded &&
                vaccineName.length > 1 ? (
                <div
                  style={{
                    backgroundColor: "#f5f5f5",
                    borderRadius: "10px",
                    padding: "10px",
                    maxHeight: "300px",
                    overflowY: "auto",
                    marginTop: "5px",
                  }}
                >
                  <div style={{ color: "red" }}>No Options Found!</div>
                </div>
              ) : null}
            </Grid>
            )}
            {fieldLayout.isFieldVisible("dose") && (
            <Grid item {...fieldLayout.getFieldGrid("dose", { xs: 12, sm: 4 })}>
              <StyledTextField
                fullWidth
                label={fieldLayout.getFieldLabel("dose", "Dose")}
                value={dose}
                onChange={(e) => setDose(e.target.value)}
                error={!!validationErrors.dose}
                helperText={validationErrors.dose}
              />
            </Grid>
            )}
            {fieldLayout.isFieldVisible("date_of_administration") && (
            <Grid item {...fieldLayout.getFieldGrid("date_of_administration", { xs: 12, sm: 3 })}>
              <VaccinationDateField
                id="dateOfAdministration"
                value={dateOfAdministration}
                onChange={setDateOfAdministration}
              />
              {validationErrors.dateOfAdministration && (
                <Typography
                  variant="caption"
                  sx={{ color: "#d32f2f", marginLeft: "14px" }}
                >
                  {validationErrors.dateOfAdministration}
                </Typography>
              )}
            </Grid>
            )}

            {/* New row for Add/Update Button */}
            <Grid
              item
              xs={12}
              sm={1.5}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <StyledButton
                variant="contained"
                onClick={editingIndex !== null ? handleUpdate : handleAdd}
                fullWidth
                bgColor="#0E3F66"
                sx={{
                  fontSize: { xs: "0.8rem", sm: "1rem" },
                  width: "100%",
                }}
              >
                {editingIndex !== null ? "Update" : "Add"}
              </StyledButton>
            </Grid>
          </Grid>
        </Card>
      )}
      <AdultVaccinationTable
        data={vaccinations}
        activeSection={activeSection}
        handleEditRow={handleEditRow}
        handleDeleteRow={handleDeleteRow}
        fieldLayout={fieldLayout}
      />
    </ThemeProvider>
  );
};

export default AdultVaccination;
